import { throttle } from 'underscore';
import React, { useEffect, useMemo, useState } from 'react';
import { prop, pick } from 'ramda';

import { Portal } from '@interlayer/js/app/components/system/Portal';
import { SvgIcon } from '@interlayer/js/app/components/common/SvgIcon';

import { TBackToTopButtonProps } from './types';
import { tagTypeService } from '@core/js/services/tagType.service';
import {
    additionalMargins,
    HEADER_AND_FOOTER_HEIGHT
} from '@interlayer/js/components/tagTypes/buzz/BackToTopButton/constants';
import { useAppSelector, usePageSelector } from '@core/js/store/hooks';
import { TAG_TYPE_BUZZ } from '@interlayer/js/components/system/TagManager/buzz/constants';
import { useIsShowTag } from '@interlayer/js/components/system/App/buzz/hooks/useIsShowTag';
import { useShowAction } from '@interlayer/js/components/system/App/buzz/hooks/useShowAction';
import { useInDesktop, useInMobile } from '@interlayer/js/app/hooks/device/hooks/useMediaQuery';
import { BOTTOM_STICKY_IDS } from '@interlayer/js/app/components/system/BottomStickyElements/constants';

import style from './BackToTopButton.scss';

export const BackToTopButton: React.FC<TBackToTopButtonProps> = ({ config: { backgroundColor, iconColor } }) => {
    const isMobile = useInMobile();
    const isDesktop = useInDesktop();
    const { isStickyFooter, portlets } = usePageSelector(pick(['isStickyFooter', 'portlets']));
    const [showButton, setShowButton] = useState(false);
    const isShowPlayBingoButton = useAppSelector(prop('isShowPlayBingoButton'));
    const isShowParcel = useIsShowTag(TAG_TYPE_BUZZ.parcelPromotion);
    const isShowBottomMenu = useIsShowTag(TAG_TYPE_BUZZ.bottomNavigation);
    const isStickyBtnFromSlotPageAddedOnPage = useMemo(() => portlets.find(item => item.portletType === 'slot-page'), [portlets]);
    const isShowBackToTopButton = useMemo(() => {
        return !isShowParcel && showButton;
    }, [isShowParcel, showButton]);

    useShowAction(TAG_TYPE_BUZZ.backToTopButton, isShowBackToTopButton);

    useEffect(() => {
        const handleScroll = throttle(() => {
            setShowButton(window.scrollY > window.innerHeight - HEADER_AND_FOOTER_HEIGHT);
        }, 200, { leading: false });

        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    const calculateButtonPosition = useMemo(() => {
        let position = additionalMargins.default;

        if (isDesktop) {
            return additionalMargins.desktop;
        }

        if (!isStickyFooter) {
            position += additionalMargins.stickyFooter;
        }

        if (isShowPlayBingoButton) {
            position += additionalMargins.playBingoButton;
        }

        if (isShowBottomMenu && isMobile) {
            position += additionalMargins.bottomMenu;
        }

        if (isStickyBtnFromSlotPageAddedOnPage && isMobile) {
            position += additionalMargins.stickyBtnFromSlotPageAddedOnPage;
        }

        return position;
    }, [isDesktop, isMobile, isShowBottomMenu, isShowPlayBingoButton, isStickyBtnFromSlotPageAddedOnPage, isStickyFooter]);

    const scrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    };

    if (!isShowBackToTopButton) {
        return null;
    }

    return (
        <Portal id={BOTTOM_STICKY_IDS.backToTop}>
            <div
                onClick={scrollToTop}
                style={{ backgroundColor, bottom: `${calculateButtonPosition}rem` }}
                className={style.wrapper}
                data-testid="BackToTopButton_wrapper"
            >
                <SvgIcon
                    type="arrow-down"
                    color={iconColor || '#fff'}
                    className={style.icon}
                />
            </div>
        </Portal>
    );
};

tagTypeService.registerTagType({ [TAG_TYPE_BUZZ.backToTopButton]: BackToTopButton });
